import { MoreVert } from "@mui/icons-material";
import { styles } from "./style";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
} from "@/common/styles/style";
import React, { useEffect, useState } from "react";
import DataModify from "@/organisms/dataModify";
import Graph from "@/atoms/graph";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import Data from "@/atoms/data";
import MenuPopover from "../../molecules/menuPopover";
import TrendModify from "@/organisms/trendModify";
import {
  apiMachineType,
  CollectedDataType,
  DragDndItemsType,
} from "@/common/type/type";
import { Routes } from "@/router/routes";
import {
  DataViewType,
  TrendViewType,
  WidgetDataType,
} from "@/pages/dashboard/type";
import { formatDate, today, lastWeek } from "@/helpers/getUtcOffset";
import { handleDelete } from "@/helpers/widgetDelete";

interface DragBoxProps {
  loading?: boolean;
  boxId: number;
  sortId: number;
  type: number;
  selectedMachine: apiMachineType;
  dragAreaItems: DragDndItemsType;
  data?: DataViewType | TrendViewType;
  setTotalWidgets: React.Dispatch<React.SetStateAction<WidgetDataType[]>>;
  modify: boolean;
  onUpdateWidgets?: (widgets: WidgetDataType[]) => void;
}

export default function DragBox({
  boxId,
  sortId,
  type,
  selectedMachine,
  dragAreaItems,
  data,
  setTotalWidgets,
  modify,
  onUpdateWidgets,
}: DragBoxProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [collectedData, setCollectedData] = useState<
    CollectedDataType[] | null
  >(null);
  const [dataWidget, setDataWidget] = useState<DataViewType>({});
  const [trendWidget, setTrendWidget] = useState<TrendViewType>({});
  const [selectedName, setSelectedName] = useState<string[]>([]);
  const startDate = formatDate(lastWeek);
  const endDate = formatDate(today);
  const isWidget: boolean = true;

  useEffect(() => {
    if (type === 1) {
      setDataWidget(data as DataViewType);
    } else {
      setTrendWidget(data as TrendViewType);
    }
  }, [type, data]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModifyPopup = () => {
    setOpen(true);
  };

  const handleClosePopup = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleDataSave = (newData: CollectedDataType[]) => {
    setCollectedData(newData);
    const newDataNames = newData.map((data) => data.dataName);
    setSelectedName(newDataNames);
  
    setTotalWidgets((prevWidgets) => {
      const isTrend = type === 2;
    
      // widgetがすでに存在するかチェック
      const existing = prevWidgets.find(
        (w) => w.machineId === selectedMachine.id
      );
    
      let updated: WidgetDataType[];
    
      if (existing) {
        // 既存のデータに対して更新
        const updatedWidgets = prevWidgets.map((widget) => {
          if (widget.machineId === selectedMachine.id) {
            const updatedDataWidgets = widget.dataWidgets.map((item) =>
              item.sortId === sortId
                ? { ...item, dataNames: newDataNames }
                : item
            );
    
            const updatedTrendWidgets = widget.trendWidgets.map((item) => {
              const names = newDataNames.slice(0, 5);
              return item.sortId === sortId
                ? {
                    ...item,
                    dataName1: names[0] || null,
                    dataName2: names[1] || null,
                    dataName3: names[2] || null,
                    dataName4: names[3] || null,
                    dataName5: names[4] || null,
                  }
                : item;
            });
    
            return {
              ...widget,
              dataWidgets: updatedDataWidgets,
              trendWidgets: updatedTrendWidgets,
            };
          }
          return widget;
        });
    
        updated = updatedWidgets;
      } else {
        // 新規作成パターン
        updated = [
          ...prevWidgets,
          {
            machineId: selectedMachine.id,
            dataWidgets: isTrend
              ? []
              : [
                  {
                    sortId,
                    dataNames: newDataNames,
                    position: data?.position ?? 0,
                  },
                ],
            trendWidgets: isTrend
              ? [
                  {
                    sortId,
                    dataName1: newDataNames[0] || null,
                    dataName2: newDataNames[1] || null,
                    dataName3: newDataNames[2] || null,
                    dataName4: newDataNames[3] || null,
                    dataName5: newDataNames[4] || null,
                    position: data?.position ?? 0,
                  },
                ]
              : [],
          },
        ];
      }
    
      onUpdateWidgets?.(updated);
    
      return updated;
    });
  };

  const handleDeleteClose = () => {
    handleDelete(boxId, selectedMachine.id, dragAreaItems, setTotalWidgets);
    setIsDeleted(true);
    setAnchorEl(null);
  };

  if (isDeleted) {
    return null;
  }

  return (
    <Card sx={styles.container(type)}>
      <CardHeader
        action={
          modify ? (
            <IconButton aria-label="settings" onClick={handleClick}>
              <MoreVert />
            </IconButton>
          ) : null
        }
        title={
          type === 2
            ? `${selectedMachine.machineNumber} トレンド`
            : `${selectedMachine.machineNumber} データ`
        }
        sx={styles.header}
      />
      <MenuPopover
        anchorEl={anchorEl}
        handleMenuClose={handleClose}
        data={[
          { name: "編集", func: handleModifyPopup },
          { name: "削除", func: handleDeleteClose },
        ]}
      />
      {type === 2 ? (
        <>
          <CardContent sx={styles.graph()}>
            <Graph
              selectedNames={selectedName}
              saved={trendWidget?.data || []}
              isWidget={isWidget}
              startDate={startDate}
              endDate={endDate}
            />
          </CardContent>
          <CardActions sx={styles.cardActions()}>
            <Button
              onClick={() =>
                navigate(Routes.TREND_EXPAND, {
                  state: {
                    trendWidget: trendWidget,
                  },
                })
              }
            >
              <Typography variant="button">explore</Typography>
              <ArrowForwardIos sx={styles.arrowIcon()} />
            </Button>
            <Typography variant="body1" sx={styles.actionText()}>
              {startDate} から {endDate} まで
            </Typography>
          </CardActions>
          <TrendModify
            open={open}
            selectedMachine={selectedMachine as apiMachineType}
            onSave={handleDataSave}
            onClose={handleClosePopup}
            selectedNames={
              trendWidget?.data?.map((item) => Object.keys(item)[0]) ?? []
            }
          />
        </>
      ) : (
        <>
          <CardContent sx={styles.data()}>
            <Data collected={collectedData || []} saved={dataWidget} />
          </CardContent>
          <CardActions></CardActions>
          <DataModify
            open={open}
            selectedMachine={selectedMachine as apiMachineType}
            onSave={handleDataSave}
            onClose={handleClosePopup}
            selectedNames={
              dataWidget?.data?.map((item) => Object.keys(item)[0]) ?? []
            }
          />
        </>
      )}
    </Card>
  );
}
